'use client';

import LandingLayout from '@/components/LandingLayout';
import { salonFaqs } from '@/data/industryFaqs';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView, trackPhoneClick } from '@/lib/analytics';
import {
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  HeartIcon,
  PhoneIcon,
  SparklesIcon,
  StarIcon,
  UserGroupIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function SalonLanding() {
  useEffect(() => {
    trackPageView('Salon Landing', undefined, { funnel_step: 'industry_landing' });
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { isAuthenticated } = useAuth();

  const painPoints = [
    {
      problem: "Phone ringing during appointments-customers hate that interruption",
      impact: "Create friction or miss calls entirely"
    },
    {
      problem: "Answering 'What's the price for highlights?' 100+ times daily",
      impact: "10-15 hours weekly on the same questions"
    },
    {
      problem: "No-show appointments cost you $50-300 per slot",
      impact: "5-10 no-shows per week = $250-3,000 lost revenue"
    },
    {
      problem: "Weekend/evening calls when you're closed interrupt your personal life",
      impact: "Can't enjoy weekends without work bleeding in"
    }
  ];

  const salonBenefits = [
    {
      title: "Book Appointments 24/7",
      description: "Clients book haircuts, color appointments, and spa services at midnight or 6 AM. AI handles it all, sends confirmations, reduces no-shows by 40%+.",
      icon: CalendarIcon,
      stat: "24/7"
    },
    {
      title: "Save 12-18 Hours Weekly",
      description: "Stop answering calls about pricing, availability, and service questions. AI provides perfect information every time, books the appointment.",
      icon: ClockIcon,
      stat: "12-18 hrs"
    },
    {
      title: "Cut No-Shows by 40%+",
      description: "Automatic confirmation texts/calls, personalized reminders before appointments. Clients remember and show up.",
      icon: CheckCircleIcon,
      stat: "40%+"
    },
    {
      title: "Capture Impulse Bookings",
      description: "Client browsing on Instagram at 9 PM wants highlights tomorrow. With AI, they book instantly instead of calling competitor in the morning.",
      icon: SparklesIcon,
      stat: "24/7"
    },
    {
      title: "Handle Complex Color Appointments",
      description: "AI knows root touch-ups take 45 mins, full color takes 2 hours, highlights take 3 hours. Books accurately every time, asks about desired shades.",
      icon: UserGroupIcon,
      stat: "100%"
    },
    {
      title: "Manage Stylist Preferences & Specialties",
      description: "Clients request specific stylists. AI routes appointments intelligently: Maria does balayage, Sarah does precision cuts, Amelia does bridal makeup.",
      icon: HeartIcon,
      stat: "Smart routing"
    }
  ];

  const scenarios = [
    {
      title: "The Weekend Booking You Always Miss",
      problem: "Saturday 7 PM: Client scrolling Instagram, sees your salon's portfolio, loves it. Wants an appointment. Calls-voicemail. Googles competitors. Calls one with a live answer. That's your $150 appointment + follow-up work, gone.",
      solution: "With Greet: AI answers 24/7. Client describes what they want. AI offers available times, sends booking confirmation. You're already on their calendar for Monday."
    },
    {
      title: "The Color Consultation You Nailed (By Accident)",
      problem: "Client calls asking for 'subtle highlights.' You're with someone. By the time you call back, they've already booked with a competitor who answered immediately.",
      solution: "With Greet: AI answers, asks the right questions-current color, desired look, previous damage-captures the full picture. You call back prepped with all details. Better consultation, better result, client books."
    },
    {
      title: "No-Show Wednesday Disaster",
      problem: "You have 6 appointments Wednesday. 2 no-shows. That's $300 in lost revenue. You could've filled those slots if you knew earlier.",
      solution: "With Greet: Automatic appointment confirmations and reminder messages 24 hours before. No-show rate drops 40%+. Same 6 appointments = $300 kept in your pocket."
    }
  ];

  const testimonials = [
    {
      name: "Jessica Chen",
      business: "Luxe Hair Studio",
      stars: 5,
      quote: "We went from 4-5 calls during services we'd miss to zero interruptions. Greet books appointments from Instagram browswers at 9 PM who become paying clients. Our chair utilization is up 35%.",
      metric: "+$5,000/month in new bookings"
    },
    {
      name: "Maria Rodriguez",
      business: "Bella Beauty Salon",
      stars: 5,
      quote: "Color appointments are complex-duration, price, stylist expertise matter. Greet handles it perfectly. No more back-and-forth. Clients love the convenience and booking accuracy improved 90%.",
      metric: "90% fewer booking errors"
    },
    {
      name: "Amanda Walsh",
      business: "Elite Spa & Salon",
      stars: 5,
      quote: "No-shows were killing us. Greet's reminder texts cut them from 8-10 per week to 2-3. That's $2,000+ per week saved. Plus, we stopped missing evening calls entirely. Game changer.",
      metric: "$2,000+/week no-show reduction"
    }
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-purple-900 via-pink-900 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-pink-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-pink-500/20 border border-pink-400/30 px-4 py-2 text-sm font-semibold text-pink-100">
              <span className="h-2 w-2 rounded-full bg-pink-400"></span>
              #1 AI Receptionist for Salons & Spas
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Never Miss a Booking Again
            </h1>
            <p className="text-xl text-pink-100 mb-8 max-w-2xl leading-relaxed">
              Your AI receptionist books 24/7 appointments, reduces no-shows by 40%+, handles complex services, and fills empty chair time-while you focus on making clients beautiful.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                href={isAuthenticated ? "/app" : "/register"}
                onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "salon_hero")}
                className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-block"
              >
                {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              </Link>
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'salon_demo')}
                className="bg-white/10 border border-white text-white hover:bg-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <PhoneIcon className="h-5 w-5 mr-2" />
                See Live Demo: (415) 877-3166
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Salon Booking Challenge
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every empty chair is lost revenue. Every no-show is money gone. Every missed call is a client choosing your competitor.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <div key={index} className="border-l-4 border-red-500 pl-6 py-4">
                <div className="flex items-start gap-4">
                  <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{point.problem}</h3>
                    <p className="text-red-600 font-semibold">{point.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Greet Transforms Your Salon
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              More bookings. Fewer no-shows. Full chair time. Actually enjoying your evenings and weekends.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {salonBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center mb-6 shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <benefit.icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                </div>
                <div className="inline-block px-4 py-1 bg-pink-100 rounded-full mb-4 transition-all duration-300 group-hover:bg-pink-200 group-hover:scale-105">
                  <span className="text-2xl font-bold text-pink-900 transition-colors duration-300 group-hover:text-pink-900">{benefit.stat}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 transition-colors duration-300 group-hover:text-pink-600">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Scenarios */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Salons Actually Use Greet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real scenarios. Real revenue. Real work-life balance.
            </p>
          </div>
          
          <div className="space-y-8">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-8 border border-pink-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{scenario.title}</h3>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">Without Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.problem}</p>
                  </div>
                  
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900">With Greet</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{scenario.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Salon Owners Love Greet
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real salon owners. Real revenue growth. Real peace of mind.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current transition-colors duration-300 group-hover:text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed transition-colors duration-300 group-hover:text-gray-900">"{testimonial.quote}"</p>
                <div className="bg-pink-100 rounded-lg px-4 py-3 mb-4 transition-colors duration-300 group-hover:bg-pink-200">
                  <p className="text-pink-900 font-semibold text-sm">{testimonial.metric}</p>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 transition-colors duration-300 group-hover:text-pink-600">{testimonial.name}</div>
                  <div className="text-gray-600 text-sm transition-colors duration-300 group-hover:text-gray-700">{testimonial.business}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-gray-600 mb-10">
              Comparing phone coverage options?{' '}
              <Link href="/salon-answering-service" className="text-pink-700 font-semibold hover:text-pink-900">
                See our salon answering service guide
              </Link>
              {' '}or{' '}
              <Link href="/ai-receptionist-pricing" className="text-pink-700 font-semibold hover:text-pink-900">
                AI receptionist pricing
              </Link>
              .
            </p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Questions from Salon Owners
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about AI call handling and booking for salons.
            </p>
          </div>
          
          <div className="space-y-4">
            {salonFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDownIcon 
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                <div className={`px-6 text-gray-600 border-t border-gray-200 bg-gray-50 transition-all duration-300 ${
                  openFaq === index ? 'pb-4 max-h-96' : 'pb-0 max-h-0'
                } overflow-hidden`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-pink-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Leaving Money on the Table
          </h2>
          <p className="text-xl text-pink-100 mb-8 max-w-2xl mx-auto">
            Every empty chair is revenue you won't get back. Start booking 24/7 and cut no-shows in half. Try it free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              href={isAuthenticated ? "/app" : "/register"}
              onClick={() => trackButtonClick(isAuthenticated ? "go_to_dashboard" : "start_free_trial", "salon_cta")}
              className="bg-white text-pink-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'salon_cta')}
              className="bg-transparent border border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Call Demo: (415) 877-3166
            </a>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
} 
